/**
 * @fileoverview Reactivate User form.
 * @description The Reactivate dialog's form for one version of the user's details: the notes, the
 * role and manager, then the reason and note. Presentational: the form logic is in
 * useReactivateUserForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import LifecycleReasonFields from '@/features/users/components/LifecycleReasonFields'
import ReactivationNotes from '@/features/users/components/ReactivationNotes'
import ReactivationPlacementFields from '@/features/users/components/ReactivationPlacementFields'
import {
  REACTIVATE_FORM_ID,
  REACTIVATE_TEST_ID,
} from '@/features/users/constants/lifecycle.constants'
import { useReactivateUserForm } from '@/features/users/hooks/useReactivateUserForm'

import type {
  LifecycleReason,
  ReactivateUserRequest,
  ReactivationRoleOption,
  UserDetail,
} from '@/features/users/types/users.types'

interface ReactivateUserFormProps {
  user: UserDetail
  roles: ReactivationRoleOption[]
  reasons: LifecycleReason[]
  noteMaxLength: number
  onSubmit: (payload: ReactivateUserRequest) => void
}

/**
 * @component
 * @param {ReactivateUserFormProps} props - The user, the role choices, the reasons, the note limit
 * and the submit handler.
 * @param {UserDetail} props.user - The user coming back.
 * @param {ReactivationRoleOption[]} props.roles - The roles the admin may choose.
 * @param {LifecycleReason[]} props.reasons - The reasons to choose from.
 * @param {number} props.noteMaxLength - The longest the note may be.
 * @param {(payload: ReactivateUserRequest) => void} props.onSubmit - Called with the request once
 * the form is valid.
 */
function ReactivateUserForm({
  user,
  roles,
  reasons,
  noteMaxLength,
  onSubmit: submitRequest,
}: ReactivateUserFormProps) {
  const logic = useReactivateUserForm({ user, roles, noteMaxLength, onSubmit: submitRequest })
  const { form, managersQuery } = logic
  const { register } = form

  return (
    <form
      id={REACTIVATE_FORM_ID}
      data-testid={`${REACTIVATE_TEST_ID}-form`}
      onSubmit={logic.onSubmit}
      noValidate
      className="flex flex-col gap-4"
    >
      <ReactivationNotes user={user} />
      <ReactivationPlacementFields
        roles={roles}
        roleRegistration={register('roleId', { onChange: logic.onRoleChange })}
        managerRegistration={register('reportingManagerUserId')}
        roleError={logic.roleError}
        managerError={logic.managerError}
        managers={managersQuery.data}
        managersLoaded={managersQuery.isSuccess}
        managersError={managersQuery.error}
      />
      <LifecycleReasonFields
        idPrefix="reactivateUser"
        testIdPrefix={REACTIVATE_TEST_ID}
        reasons={reasons}
        reasonRegistration={register('reasonCode')}
        noteRegistration={register('reasonNote')}
        noteRequired={logic.noteRequired}
        noteMaxLength={noteMaxLength}
        reasonError={logic.reasonError}
        noteError={logic.noteError}
      />
    </form>
  )
}

export default ReactivateUserForm
